/* eslint-disable max-lines */
import { User, UserDTO, UserListFilters } from '@hakimo-ui/hakimo/types';
import { Column, TableData } from '@hakimo-ui/hakimo/ui-table';
import { Button, Label, Selectable } from '@hakimo-ui/shared/ui-base';
import { ChevronDownIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TenantsDropdownProps {
  tenant: Selectable;
  mspTenants?: Selectable[] | null;
}

function TenantsDropdown({ tenant, mspTenants }: TenantsDropdownProps) {
  // Combine primary tenant and MSP tenants, removing duplicates
  const allTenants = [tenant, ...(mspTenants || [])].filter(
    (t, index, array) => array.findIndex((item) => item.id === t.id) === index
  );

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4, // Add 4px gap
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160), // Minimum width of 160px
      });
    }
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  if (allTenants.length === 1) {
    return <span>{allTenants[0].name}</span>;
  }

  const maxHeight = allTenants.length <= 2 ? 'auto' : '68px';

  const dropdownContent = isOpen ? (
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        minWidth: position.width,
        zIndex: 9999,
        maxHeight: maxHeight,
      }}
      className="overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      {allTenants.map((tenantItem) => (
        <div
          key={tenantItem.id}
          className="cursor-default whitespace-nowrap px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {tenantItem.name}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className="flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm font-medium transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700"
        type="button"
      >
        <span>{`${allTenants.length} tenants`}</span>
        <ChevronDownIcon
          className={`h-3 w-3 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {typeof window !== 'undefined' &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}

export const columns: Column[] = [
  {
    id: 'employeeId',
    name: 'Employee ID',
  },
  {
    id: 'name',
    name: 'Name',
  },
  {
    id: 'email',
    name: 'E-Mail',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'tenants',
    name: 'Tenants',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'role',
    name: 'Role',
    showAtBreakpoint: 'md',
  },
  {
    id: 'mspLocations',
    name: 'MSP Locations',
    showAtBreakpoint: 'md',
  },
  {
    id: 'alarmTypeGroups',
    name: 'Alarm Type Groups',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'action',
    name: '',
    showAtBreakpoint: 'sm',
  },
];

export function getTableData(
  items: User[],
  canUpdate: boolean,
  onClickEdit: (userId: string) => void
): TableData {
  return {
    columns: columns,
    rows: items.map((item) => ({
      id: item.id,
      cells:
        item.enabled || item.enabled === undefined
          ? [
              { value: item.employeeId || '-' },
              {
                value: item.name,
              },
              { value: item.email },
              {
                value: item.tenant ? (
                  <TenantsDropdown
                    tenant={item.tenant}
                    mspTenants={item.mspTenants}
                  />
                ) : (
                  '-'
                ),
              },
              { value: item.roles[0] },
              {
                value:
                  item.mspLocations && item.mspLocations.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {item.mspLocations.map((loc) => (
                        <Label key={loc.id} small text={loc.name} />
                      ))}
                    </div>
                  ) : (
                    '-'
                  ),
              },
              {
                value:
                  item.alarmtype_groups && item.alarmtype_groups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {item.alarmtype_groups.map((group) => (
                        <Label key={group.id} small text={group.name} />
                      ))}
                    </div>
                  ) : (
                    '-'
                  ),
              },
              {
                value: canUpdate ? (
                  <Button variant="icon" onClick={() => onClickEdit(item.id)}>
                    <PencilIcon className="w-4" />
                  </Button>
                ) : undefined,
              },
            ]
          : [
              {
                value: (
                  <span className="italic"> {item.employeeId || '-'} </span>
                ),
              },
              {
                value: <span className="italic"> {item.name} (Disabled) </span>,
              },
              {
                value: <span className="italic"> {item.email} </span>,
              },
              {
                value: item.tenant ? (
                  <span className="italic">
                    <TenantsDropdown
                      tenant={item.tenant}
                      mspTenants={item.mspTenants}
                    />
                  </span>
                ) : (
                  <span className="italic">-</span>
                ),
              },
              {
                value: <span className="italic"> {item.roles[0]} </span>,
              },
              {
                value:
                  item.mspLocations && item.mspLocations.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {item.mspLocations.map((loc) => (
                        <Label key={loc.id} small text={loc.name} />
                      ))}
                    </div>
                  ) : (
                    '-'
                  ),
              },
              {
                value:
                  item.alarmtype_groups && item.alarmtype_groups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {item.alarmtype_groups.map((group) => (
                        <Label key={group.id} small text={group.name} />
                      ))}
                    </div>
                  ) : (
                    '-'
                  ),
              },
              {
                value: undefined,
              },
            ],
    })),
  };
}

export function isNotDefaultFilter(filters: UserListFilters): boolean {
  return (
    filters.emails.length > 0 ||
    filters.names.length > 0 ||
    filters.employeeIds.length > 0 ||
    filters.roles.length > 0 ||
    filters.tenantIds.length > 0 ||
    !filters.enabled.showEnabled ||
    filters.enabled.showNotEnabled
  );
}

export const mapUserFilter = (filters: UserListFilters) => {
  const { emails, names, employeeIds, roles, tenantIds, enabled } = filters;
  const filtersApplied: Record<string, string[] | object> = {};
  emails.length > 0 && (filtersApplied['emails'] = emails);
  names.length > 0 && (filtersApplied['names'] = names);
  employeeIds.length > 0 && (filtersApplied['employeeIds'] = employeeIds);
  roles.length > 0 && (filtersApplied['roles'] = roles);
  tenantIds.length > 0 && (filtersApplied['tenantIds'] = tenantIds);
  filtersApplied['enabled'] = enabled;

  return filtersApplied;
};

export function getFormattedExportData(
  data: UserDTO
): Record<string, string>[] {
  if (!data?.items || !Array.isArray(data.items)) {
    return [];
  }

  return data.items.map((item) => {
    // Combine primary tenant and MSP tenants, removing duplicates
    const allTenants = [item.tenant, ...(item.mspTenants || [])].filter(
      (t, index, array) =>
        t && array.findIndex((tenant) => tenant.id === t.id) === index
    );

    const formattedItem = {
      'Employee ID': item.employeeId || '-',
      Name: item.name || '-',
      Email: item.email || '-',
      Tenants:
        allTenants.length > 0
          ? allTenants.map((tenant) => tenant.name).join(', ')
          : '-',
      Role: Array.isArray(item.roles)
        ? item.roles.join(', ')
        : item.roles || '-',
      'MSP Locations': Array.isArray(item.mspLocations)
        ? item.mspLocations.map((location) => location.name).join(', ')
        : '-',
      'Alarm Type Groups': Array.isArray(item.alarmtype_groups)
        ? item.alarmtype_groups.map((group) => group.name).join(', ')
        : '-',
    };

    return formattedItem;
  });
}
